import { defineStore } from 'pinia';
import { ref, computed, onScopeDispose } from 'vue';
import { isNavigationFailure, NavigationFailureType, type NavigationHookAfter } from 'vue-router';
import { businessService } from '~/services/business-service';
import { isBusinessPortalPath } from '~/utils/person-address';
import { memberFacingMessage } from '~/utils/errors';
import type {
    Business,
    BusinessSettings,
    AuthenticationSettings,
    ActivityHighlights,
} from '~/types/business';

export const useBusinessStore = defineStore('business', () => {
    const { showSuccess, showError, showWarning } = useGlobalToast();
    const router = useRouter();

    // State
    const business = ref<Business | null>(null);
    // The route slug the held business was loaded for. "Does the store hold this
    // route's company" is asked of this, never of the record's own slug, so the
    // answer is exactly the slug that was requested.
    const loadedSlug = ref('');
    const settings = ref<BusinessSettings | null>(null);
    const authSettings = ref<AuthenticationSettings | null>(null);
    const activityHighlights = ref<ActivityHighlights | null>(null);

    // Loading states
    const isLoadingBusiness = ref(false);
    const isLoadingSettings = ref(false);
    const isLoadingAuthSettings = ref(false);
    const isLoadingDashboard = ref(false);

    // Cache timestamps
    const businessCacheTimestamp = ref(0);
    const settingsCacheTimestamp = ref(0);
    // Track which business id the cached settings belong to, so a stale
    // cache from a previous tenant isn't reused after switching companies.
    const settingsCachedForCompanyId = ref<number | null>(null);
    const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes

    // Computed
    const currentSlug = computed(() => business.value?.slug || '');
    const companyId = computed(() => business.value?.id || 0);
    const apps = computed(() => business.value?.apps || []);
    const isEmployee = computed(() => business.value?.is_employee || false);
    const companyName = computed(() => business.value?.name || '');
    const companyLogo = computed(() => business.value?.logo || '');
    const inviteCount = computed(() => business.value?.invites || 0);

    // Cache validation
    const isCacheValid = (timestamp: number) => {
        return Date.now() - timestamp < CACHE_EXPIRY;
    };

    // Monotonic token guarding against out-of-order fetchBusiness resolutions.
    // The switcher moves A→B by changing only the slug, so an A request can
    // still be in flight when B is requested; only the latest request may
    // write the shared store, otherwise A resolving late would rewind
    // companyId/permissions to the wrong tenant.
    let latestBusinessRequestId = 0;
    // The slug that newest request asked for: the company the store is headed to,
    // which can differ from the one it holds (`loadedSlug`) while a read is out.
    let latestRequestedSlug = '';

    // The same token for settings, and for the other half of the same question:
    // `fetchSettings` refuses to write another TENANT's answer, but two reads of
    // ONE company can also overlap, and the older one landing last would rewind
    // `settings` and the cache for the rest of the TTL.
    let latestSettingsRequestId = 0;

    /**
     * Fetch business data by slug - uses company ID from auth or query param if available
     * @param slug - Company slug from URL
     * @param force - Force refresh even if cached
     * @param companyId - Optional company ID (e.g., from query param for partner companies)
     */
    async function fetchBusiness(slug: string, force = false, companyId?: number) {
        // Claim the latest-request slot up front so this call supersedes any
        // in-flight fetch — even a cache hit represents "the user now wants
        // this slug", so a slower earlier request can't clobber it.
        const requestId = ++latestBusinessRequestId;
        latestRequestedSlug = slug;

        // Return cached data if valid and not forced. This request claimed the
        // latest slot above, so it must also clear the loading flag itself —
        // an in-flight request this supersedes will see its finally block
        // guarded off and can no longer clear it.
        if (!force && loadedSlug.value === slug && isCacheValid(businessCacheTimestamp.value)) {
            isLoadingBusiness.value = false;
            return business.value;
        }

        isLoadingBusiness.value = true;
        try {
            // Determine the company ID to use. Note: `companyId` here may
            // come from the `?cid=` query param, which is user-controllable.
            // We treat it as a hint and verify the resulting business's
            // slug matches the URL slug before trusting it — otherwise a
            // tampered link `/business/{slugA}?cid={companyB}` would load
            // company B's data under slugA's routes.
            let resolvedCompanyId: number | undefined;

            // Auth user's employer is trusted (slug match implies the
            // session has access to that company).
            const authStore = useAuthStore();
            const employer = authStore.user?.employer;
            if (employer && employer.slug === slug) {
                resolvedCompanyId = employer.id;
            }

            if (!resolvedCompanyId) {
                // Partner admins hit a managed child's URL directly. Use
                // the public slug-lookup endpoint as the source of truth;
                // never derive the id from `cid` alone.
                const resolved = await businessService.getBusiness(slug);
                resolvedCompanyId = resolved?.id;

                if (companyId && resolved && resolved.id !== companyId) {
                    // The cid hint disagrees with the slug — log it and
                    // proceed with the slug-derived id rather than the
                    // potentially tampered cid.
                    console.warn(
                        `Ignoring mismatched cid=${companyId} for slug "${slug}" (resolved id=${resolved.id})`
                    );
                }
            }

            if (!resolvedCompanyId) {
                throw new Error(`Unable to resolve business for slug "${slug}".`);
            }

            const data = await businessService.getBusinessWithApps(resolvedCompanyId);

            // A newer fetchBusiness superseded this one while it was in flight;
            // return the data to this (stale) caller but don't write it into the
            // shared store, which now belongs to the newer request's tenant.
            if (requestId !== latestBusinessRequestId) {
                return data;
            }

            business.value = data;
            loadedSlug.value = slug;
            businessCacheTimestamp.value = Date.now();

            return data;
        } catch (error) {
            showError('Error', memberFacingMessage(error, 'Failed to load business data'));
            throw error;
        } finally {
            // Only the latest request may clear the loading flag — an older
            // (stale) fetchBusiness resolving after a newer slug load
            // started must not drop the skeleton while the current company
            // is still loading.
            if (requestId === latestBusinessRequestId) {
                isLoadingBusiness.value = false;
            }
        }
    }

    /**
     * Brings the store back to the company of the business page a failed navigation
     * leaves the reader on.
     *
     * `middleware/business.ts` awaits `fetchBusiness` for the next route before that
     * navigation can land or fail, so a navigation that fails can leave its company's
     * read as the newest, and that read writes the store for a page that never mounts.
     * The page still on screen then holds another company for good: its
     * `routeCompanyId` (`useTenantScopedLoad`) stays null, which disables every write
     * it offers, and the sidebar names the other company. A click on the link to the
     * page being left while a switch is held there is enough: Vue Router cancels the
     * switch and runs no guard for the duplicate, so no middleware asks for the page's
     * own company.
     *
     * The ask claims the newest-request slot as it starts, so a read for another
     * company still out is discarded when it answers; it is a cache hit while the store
     * holds the page's company, and reads the company back once another's answer has
     * landed. No `?cid=` hint goes with it: `fetchBusiness` resolves the company from
     * the slug alone, and a hint only adds a warning when it disagrees.
     */
    const holdCompanyOnScreen: NavigationHookAfter = (_to, _from, failure) => {
        // A navigation that lands has its company asked for by the middleware and by
        // its page. One cancelled by a newer navigation is that one's to settle: its
        // own hook runs once it has landed or failed in turn.
        if (!failure || isNavigationFailure(failure, NavigationFailureType.cancelled)) return;

        // The router's route, not the hook's `from`: the page the reader is on now.
        const onScreen = router.currentRoute.value;
        const slug = onScreen.params.slug;
        if (!isBusinessPortalPath(onScreen.path) || typeof slug !== 'string') return;

        // Nothing newer than the page's own company was asked for, and the store holds
        // it or is reading it: a click on the page's own link with nothing out reads
        // nothing.
        if (latestRequestedSlug === slug && (loadedSlug.value === slug || isLoadingBusiness.value)) return;

        fetchBusiness(slug).catch((error: unknown) => {
            // `fetchBusiness` has shown the failure; the page waits for its company.
            console.error('Failed to load the business on screen again:', error);
        });
    };

    const stopHoldingCompanyOnScreen = router.afterEach(holdCompanyOnScreen);
    onScopeDispose(stopHoldingCompanyOnScreen);

    type SettingsReadContext = 'load' | 'after-save';

    function reportSettingsReadFailure(error: unknown, context: SettingsReadContext, fallback: string) {
        if (context === 'after-save') {
            showWarning('Settings saved', "We couldn't refresh the settings. Retry loading them before making another change.");
            return;
        }
        showError('Error', memberFacingMessage(error, fallback));
    }

    /** Public reads report an unavailable page; a saved write reports an unavailable refresh. */
    function fetchSettings(force = false) {
        return readSettings(force, 'load');
    }

    async function readSettings(force: boolean, context: SettingsReadContext) {
        if (!business.value?.id) {
            throw new Error('Business not loaded');
        }

        // Cache hit only when the cached settings actually belong to the
        // currently active business id — otherwise switching tenants in
        // the SPA could serve another company's settings for up to the
        // cache TTL.
        if (
            !force
            && settings.value
            && settingsCachedForCompanyId.value === business.value.id
            && isCacheValid(settingsCacheTimestamp.value)
        ) {
            return settings.value;
        }

        // Capture the id this request is for before the await — reading
        // business.value.id again after the await would tag (or serve) the
        // response under whatever tenant the store has moved on to.
        const requestedCompanyId = business.value.id;

        // The company is only half of the ownership question. Two reads of the SAME
        // company can overlap — a page that reloads after each write starts one per
        // write — and the older snapshot landing last would rewind `settings` and the
        // cache for the whole TTL, hiding a department or domain that was just
        // created from every surface reading them. Same guard `fetchBusiness` above
        // already uses. A superseded read is still RETURNED to its own caller, which
        // decides for itself whether it is still wanted; only what it would write for
        // EVERYBODY is withheld.
        const requestId = ++latestSettingsRequestId;

        /**
         * Whether this read still speaks for the store: the right company, and no newer
         * read of it started. Both halves, because either one alone lets a stale answer
         * through — one guards a tenant switch, the other two reads of one tenant.
         */
        const ownsTheStore = () => business.value?.id === requestedCompanyId && requestId === latestSettingsRequestId;

        isLoadingSettings.value = true;
        try {
            const data = await businessService.getSettings(requestedCompanyId);

            // The store switched to another company, or a newer read of this one
            // started, while this request was in flight: return the data to this
            // (stale) caller but don't write it, or tag the cache with it, otherwise a
            // slower request would poison the cache entry for the full TTL.
            if (!ownsTheStore()) {
                return data;
            }

            settings.value = data;
            settingsCacheTimestamp.value = Date.now();
            settingsCachedForCompanyId.value = requestedCompanyId;
            return data;
        } catch (error) {
            // A toast is shared state too, and the loudest kind: the caller cannot take
            // it back, and a page's own stale-error guard runs long after it is on
            // screen. So a read that no longer speaks for the store does not speak to
            // the member either — a superseded reload failing after the newest one
            // succeeded would otherwise say "Failed to load settings" over a grid that
            // is correct and current. The error is still THROWN, so the caller that
            // asked for this read learns it failed and decides for itself.
            if (ownsTheStore()) {
                reportSettingsReadFailure(error, context, 'Failed to load settings');
            }

            throw error;
        } finally {
            // Deliberately NOT `ownsTheStore()`: this is the "nothing newer is coming"
            // question, and a read whose company has since changed is still the last
            // one out. Requiring the company too would leave the flag stuck true after
            // a switch that started no new read of the settings.
            if (requestId === latestSettingsRequestId) {
                isLoadingSettings.value = false;
            }
        }
    }

    /**
     * Save settings, then refresh the shared copy because the write returns no post-state.
     * A refused write rejects. A saved write returns whether its refresh succeeded, so callers
     * can offer their read retry without reporting a failed save or reverting persisted values.
     */
    async function updateSettings(
        newSettings: Partial<BusinessSettings>,
        successMessage = 'Settings updated successfully',
    ): Promise<boolean> {
        const requestedCompanyId = business.value?.id;
        if (!requestedCompanyId) throw new Error('Business not loaded');

        try {
            await businessService.updateSettings(requestedCompanyId, newSettings);
        } catch (error) {
            if (business.value?.id === requestedCompanyId) {
                showError('Error', memberFacingMessage(error, 'Failed to update settings'));
            }
            throw error;
        }

        if (business.value?.id !== requestedCompanyId) return false;
        // The saved settings make the cached image stale even if the refresh cannot finish.
        settingsCacheTimestamp.value = 0;
        try {
            await readSettings(true, 'after-save');
        } catch {
            return false;
        }
        if (business.value?.id !== requestedCompanyId) return false;
        showSuccess('Success', successMessage);
        return true;
    }

    /**
     * Upload business logo
     */
    async function uploadLogo(file: File) {
        if (!business.value?.id) {
            throw new Error('Business not loaded');
        }

        // Capture the id this upload is for: if the user switches company
        // while it is in flight, the response must not write company A's logo
        // (or its success/error toast) into company B's live store state. The
        // upload itself stays correctly scoped to A server-side.
        const requestedCompanyId = business.value.id;

        try {
            const logoUrl = await businessService.uploadLogo(requestedCompanyId, file);

            if (business.value?.id === requestedCompanyId) {
                if (settings.value) {
                    settings.value.logo = logoUrl;
                }
                business.value.logo = logoUrl;
                showSuccess('Success', 'Logo uploaded successfully');
            }
            return logoUrl;
        } catch (error) {
            if (business.value?.id === requestedCompanyId) {
                showError('Error', memberFacingMessage(error, 'Failed to upload logo'));
            }
            throw error;
        }
    }

    /**
     * Fetch authentication settings
     */
    function fetchAuthSettings() {
        return readAuthSettings('load');
    }

    async function readAuthSettings(context: SettingsReadContext) {
        if (!business.value?.id) {
            throw new Error('Business not loaded');
        }

        // Capture the id this request is for before the await, so a
        // company switch mid-flight can be detected once the response
        // arrives instead of re-reading the (by then different) store id.
        const requestedCompanyId = business.value.id;

        isLoadingAuthSettings.value = true;
        try {
            const data = await businessService.getAuthenticationSettings(requestedCompanyId);

            // The store switched to another company while this request was
            // in flight; return the data to this (stale) caller but don't
            // overwrite the current tenant's auth/SSO/PIN/SCIM settings
            // with the previous tenant's response.
            if (business.value?.id !== requestedCompanyId) {
                return data;
            }

            authSettings.value = data;
            return data;
        } catch (error) {
            if (business.value?.id === requestedCompanyId) {
                reportSettingsReadFailure(error, context, 'Failed to load authentication settings');
            }
            throw error;
        } finally {
            isLoadingAuthSettings.value = false;
        }
    }

    /** A saved authentication change stays saved when its settings refresh fails. */
    async function updateAuthSettings(newSettings: Partial<AuthenticationSettings>): Promise<boolean> {
        const requestedCompanyId = business.value?.id;
        if (!requestedCompanyId) throw new Error('Business not loaded');

        try {
            await businessService.updateAuthenticationSettings(requestedCompanyId, newSettings);
        } catch (error) {
            if (business.value?.id === requestedCompanyId) {
                showError('Error', memberFacingMessage(error, 'Failed to update authentication settings'));
            }
            throw error;
        }

        if (business.value?.id !== requestedCompanyId) return false;
        try {
            await readAuthSettings('after-save');
        } catch {
            return false;
        }
        return business.value?.id === requestedCompanyId;
    }

    /**
     * Fetch dashboard data (activity highlights and recommended steps)
     */
    async function fetchDashboardData() {
        if (!business.value?.id) {
            return;
        }

        // Capture the id this request is for before the await — reading
        // business.value.id again after the await would tag (or serve) the
        // response under whatever tenant the store has moved on to.
        const requestedCompanyId = business.value.id;

        // Clear the previous tenant's dashboard data up front so a page
        // watching this store never renders A's highlights under B's slug
        // while this request is in flight or if it fails.
        activityHighlights.value = null;

        isLoadingDashboard.value = true;
        try {
            const highlights = await businessService.getActivityHighlights(requestedCompanyId);

            // The store switched to another company while this request was
            // in flight; don't write the previous tenant's activity highlights
            // into the store.
            if (business.value?.id !== requestedCompanyId) {
                return;
            }

            activityHighlights.value = highlights;
        } catch (error) {
            // Don't show error for dashboard data - it's optional
            console.error('Failed to load dashboard data:', error);
        } finally {
            isLoadingDashboard.value = false;
        }
    }

    /**
     * Check if user has permission. By default returns true if either
     * `read ${permission}` or `write ${permission}` is granted (the
     * "any access" check used for navigation gating). Pass an explicit
     * action ('read' | 'write') to gate mutating UI behind write-only
     * permissions.
     */
    function hasPermission(permission: string, action?: 'read' | 'write'): boolean {
        const authStore = useAuthStore();
        const user = authStore.user;

        // PB admin has all permissions
        if (user?.identity?.is_pb) {
            return true;
        }

        const employerPermissions = user?.employer?.permissions || [];
        if (action === 'read') {
            return employerPermissions.includes(`read ${permission}`) ||
                   employerPermissions.includes(`write ${permission}`);
        }
        if (action === 'write') {
            return employerPermissions.includes(`write ${permission}`);
        }
        return employerPermissions.includes(`read ${permission}`) ||
               employerPermissions.includes(`write ${permission}`);
    }

    return {
        // State
        business,
        loadedSlug,
        settings,
        authSettings,
        activityHighlights,

        // Loading states
        isLoadingBusiness,
        isLoadingSettings,
        isLoadingAuthSettings,
        isLoadingDashboard,

        // Computed
        currentSlug,
        companyId,
        apps,
        isEmployee,
        companyName,
        companyLogo,
        inviteCount,

        // Actions
        fetchBusiness,
        fetchSettings,
        updateSettings,
        uploadLogo,
        fetchAuthSettings,
        updateAuthSettings,
        fetchDashboardData,
        hasPermission,
    };
});

// HMR support
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useBusinessStore, import.meta.hot));
}
